import React, { useState } from "react";
import {
    Search,
    FileText,
    CheckCircle2,
    Package,
    Truck,
    Clock,
    ChevronRight,
    MapPin,
    IndianRupee,
    Star,
    Cog,
    MessageSquare,
    TrendingUp,
    Activity,
    Bell,
    Wrench,
    ArrowUpRight,
    ArrowDownRight,
    Circle,
} from "lucide-react";

// ---- Sample data (swap for real API data) ----------------------------------
const vendor = {
    name: "Veltrix Electronics Pvt Ltd",
    location: "Vizg, AP",
    rating: 4.8,
    reviews: 142,
    memberSince: "2023",
};

const machines = [
    { id: "M-01", name: "CNC Milling Machine — HAAS VF2", status: "running", utilization: 92 },
    { id: "M-02", name: "SMT Pick & Place Line", status: "running", utilization: 78 },
    { id: "M-03", name: "Reflow Oven — 8 Zone", status: "idle", utilization: 34 },
    { id: "M-04", name: "Laser Cutting Unit", status: "maintenance", utilization: 0 },
    { id: "M-05", name: "Injection Moulding Press", status: "running", utilization: 65 },
    { id: "M-06", name: "3-Axis Robotic Welder", status: "idle", utilization: 12 },
];

const recentOrders = [
    {
        id: "APIC-ORD-00081",
        item: "Smart IoT Water Sensor",
        buyer: "Apex Robotics Pvt Ltd",
        amount: 178000,
        status: "pending_acceptance",
        date: "2026-07-20",
    },
    {
        id: "APIC-ORD-00080",
        item: "Precision Steel Brackets",
        buyer: "Sharma Auto Components",
        amount: 62000,
        status: "in_production",
        date: "2026-07-18",
    },
    {
        id: "APIC-ORD-00079",
        item: "ABS Enclosure Set (Grey)",
        buyer: "PrintForm Buyers Co.",
        amount: 41500,
        status: "shipped",
        date: "2026-07-15",
    },
    {
        id: "APIC-ORD-00076",
        item: "Custom Wiring Harness",
        buyer: "Anand Mobility Ltd",
        amount: 95000,
        status: "delivered",
        date: "2026-07-05",
    },
];

const chats = [
    {
        id: "c1",
        name: "Apex Robotics Pvt Ltd",
        lastMessage: "Can you confirm the delivery date for the sensor batch?",
        time: "10:42 AM",
        unread: 2,
    },
    {
        id: "c2",
        name: "Sharma Auto Components",
        lastMessage: "Attaching the revised drawing for the brackets.",
        time: "9:15 AM",
        unread: 0,
    },
    {
        id: "c3",
        name: "Anand Mobility Ltd",
        lastMessage: "Payment has been released. Thank you!",
        time: "Yesterday",
        unread: 0,
    },
    {
        id: "c4",
        name: "Metro Industrial Buyers",
        lastMessage: "What's your MOQ for aluminium heat sinks?",
        time: "Yesterday",
        unread: 1,
    },
];

// ---- Status configuration ---------------------------------------------------
const STATUS_CONFIG = {
    pending_acceptance: { label: "Awaiting Buyer", bg: "#FFF7E6", text: "#B45309", dot: "#F59E0B" },
    in_production: { label: "In Production", bg: "#E9F7FE", text: "#0369A1", dot: "#0EA5E9" },
    shipped: { label: "Shipped", bg: "#F3EEFE", text: "#6D28D9", dot: "#8B5CF6" },
    delivered: { label: "Delivered", bg: "#EAFBF1", text: "#15803D", dot: "#22C55E" },
};

const MACHINE_STATUS = {
    running: { label: "Running", color: "#22C55E" },
    idle: { label: "Idle", color: "#94A3B8" },
    maintenance: { label: "Maintenance", color: "#EF4444" },
};

const currency = (n) => "\u20B9" + n.toLocaleString("en-IN");
const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

// ---- Custom scrollbar --------------------------------------------------------
function ScrollbarStyles() {
    return (
        <style>{`
      .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
      .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background-color: #CBD5E1;
        border-radius: 9999px;
        border: 2px solid transparent;
        background-clip: padding-box;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover { background-color: #94A3B8; }
      .custom-scrollbar { scrollbar-width: thin; scrollbar-color: #CBD5E1 transparent; }
    `}</style>
    );
}

// ---- Stat card ----------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon, accent, trend }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold tracking-wider text-slate-400 uppercase">
                    {label}
                </span>
                <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: accent + "15" }}
                >
                    <Icon size={16} style={{ color: accent }} />
                </div>
            </div>
            <div className="flex items-end justify-between">
                <span className="text-2xl font-bold text-slate-900">{value}</span>
                {trend && (
                    <span
                        className={`flex items-center gap-0.5 text-xs font-semibold ${trend.dir === "up" ? "text-emerald-600" : "text-rose-500"
                            }`}
                    >
                        {trend.dir === "up" ? (
                            <ArrowUpRight size={13} />
                        ) : (
                            <ArrowDownRight size={13} />
                        )}
                        {trend.value}
                    </span>
                )}
            </div>
            {sub && <span className="text-xs text-slate-400">{sub}</span>}
        </div>
    );
}

// ---- Main page -----------------------------------------------------------------
export default function VendorDashboard() {
    const [query, setQuery] = useState("");

    const stats = {
        totalMachines: machines.length,
        runningMachines: machines.filter((m) => m.status === "running").length,
        activeOrders: recentOrders.filter((o) =>
            ["pending_acceptance", "in_production", "shipped"].includes(o.status)
        ).length,
        totalOrders: recentOrders.length + 63, // + historical
        revenueThisMonth: 486200,
        unreadChats: chats.reduce((s, c) => s + c.unread, 0),
    };

    const filteredOrders = recentOrders.filter((o) => {
        const q = query.toLowerCase();
        return (
            o.item.toLowerCase().includes(q) ||
            o.id.toLowerCase().includes(q) ||
            o.buyer.toLowerCase().includes(q)
        );
    });

    return (
        <div className="h-screen overflow-y-auto custom-scrollbar bg-slate-50 py-8 px-6 font-sans">
            <ScrollbarStyles />
            <div className="max-w-6xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            Welcome back, {vendor.name.split(" ")[0]}
                        </h1>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                                <MapPin size={13} /> {vendor.location}
                            </span>
                            <span className="mx-0.5">&middot;</span>
                            <span className="flex items-center gap-1">
                                <Star size={13} className="fill-amber-400 text-amber-400" />
                                {vendor.rating} ({vendor.reviews} reviews)
                            </span>
                            <span className="mx-0.5">&middot;</span>
                            <span>Member since {vendor.memberSince}</span>
                        </div>
                    </div>
                    <button className="relative inline-flex items-center gap-1.5 border border-slate-200 bg-white text-slate-600 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-slate-50">
                        <Bell size={15} /> Notifications
                        <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                            3
                        </span>
                    </button>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                    <StatCard
                        label="Total Machines"
                        value={stats.totalMachines}
                        sub={`${stats.runningMachines} running now`}
                        icon={Cog}
                        accent="#6366F1"
                    />
                    <StatCard
                        label="Active Orders"
                        value={stats.activeOrders}
                        icon={Package}
                        accent="#0EA5E9"
                    />
                    <StatCard
                        label="Total Orders"
                        value={stats.totalOrders}
                        icon={Truck}
                        accent="#8B5CF6"
                        trend={{ dir: "up", value: "12%" }}
                    />
                    <StatCard
                        label="Revenue (MTD)"
                        value={currency(stats.revenueThisMonth)}
                        icon={IndianRupee}
                        accent="#22C55E"
                        trend={{ dir: "up", value: "8%" }}
                    />
                    <StatCard
                        label="Chats"
                        value={chats.length}
                        sub={`${stats.unreadChats} unread`}
                        icon={MessageSquare}
                        accent="#F59E0B"
                    />
                    <StatCard
                        label="Vendor Rating"
                        value={vendor.rating}
                        sub={`${vendor.reviews} reviews`}
                        icon={Star}
                        accent="#EC4899"
                    />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    {/* Left: Orders + Machines */}
                    <div className="xl:col-span-2 space-y-6">
                        {/* Recent Orders */}
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                                <h2 className="font-bold text-slate-900 text-[15px]">Recent Orders</h2>
                                <div className="relative">
                                    <Search
                                        size={14}
                                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                    />
                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search orders..."
                                        className="rounded-full border border-slate-200 bg-slate-50 pl-8 pr-3 py-1.5 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 w-44"
                                    />
                                </div>
                            </div>

                            <div className="overflow-auto max-h-[360px] custom-scrollbar">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 z-10 bg-white">
                                        <tr className="text-left text-[11px] font-semibold tracking-wider text-slate-400 uppercase border-b border-slate-100">
                                            <th className="px-6 py-3 font-semibold">Order</th>
                                            <th className="px-4 py-3 font-semibold">Buyer</th>
                                            <th className="px-4 py-3 font-semibold">Amount</th>
                                            <th className="px-4 py-3 font-semibold">Status</th>
                                            <th className="px-6 py-3 font-semibold text-right">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.map((o) => {
                                            const cfg = STATUS_CONFIG[o.status];
                                            return (
                                                <tr
                                                    key={o.id}
                                                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                                                >
                                                    <td className="px-6 py-4">
                                                        <div className="font-semibold text-slate-900">{o.item}</div>
                                                        <div className="text-xs text-slate-400 mt-0.5">{o.id}</div>
                                                    </td>
                                                    <td className="px-4 py-4 text-slate-600">{o.buyer}</td>
                                                    <td className="px-4 py-4 font-medium text-slate-800">
                                                        {currency(o.amount)}
                                                    </td>
                                                    <td className="px-4 py-4">
                                                        <span
                                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
                                                            style={{ backgroundColor: cfg.bg, color: cfg.text }}
                                                        >
                                                            <span
                                                                className="w-1.5 h-1.5 rounded-full"
                                                                style={{ backgroundColor: cfg.dot }}
                                                            />
                                                            {cfg.label}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-slate-500">
                                                        {formatDate(o.date)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredOrders.length === 0 && (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-10 text-center text-slate-400 text-sm">
                                                    No orders match your search.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Machines */}
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-bold text-slate-900 text-[15px]">Machines</h2>
                                <span className="text-xs text-slate-400">
                                    {stats.runningMachines} of {stats.totalMachines} running
                                </span>
                            </div>
                            <div className="divide-y divide-slate-50 max-h-[340px] overflow-y-auto custom-scrollbar">
                                {machines.map((m) => {
                                    const cfg = MACHINE_STATUS[m.status];
                                    return (
                                        <div
                                            key={m.id}
                                            className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/60 transition-colors"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                                                    {m.status === "maintenance" ? (
                                                        <Wrench size={16} className="text-rose-500" />
                                                    ) : (
                                                        <Activity size={16} className="text-indigo-600" />
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-medium text-slate-800 text-sm truncate">
                                                        {m.name}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                                                        <Circle size={7} fill={cfg.color} stroke="none" />
                                                        {cfg.label} &middot; {m.id}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="w-28 flex-shrink-0">
                                                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                                                    <span>Utilization</span>
                                                    <span className="font-semibold text-slate-700">{m.utilization}%</span>
                                                </div>
                                                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{
                                                            width: `${m.utilization}%`,
                                                            backgroundColor:
                                                                m.utilization > 70
                                                                    ? "#22C55E"
                                                                    : m.utilization > 30
                                                                        ? "#F59E0B"
                                                                        : "#CBD5E1",
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right: Chats */}
                    <div className="space-y-6">
                        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                                <h2 className="font-bold text-slate-900 text-[15px]">Chats</h2>
                                {stats.unreadChats > 0 && (
                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-700">
                                        {stats.unreadChats} new
                                    </span>
                                )}
                            </div>
                            <div className="divide-y divide-slate-50 max-h-[460px] overflow-y-auto custom-scrollbar">
                                {chats.map((c) => (
                                    <button
                                        key={c.id}
                                        className="w-full text-left px-6 py-4 flex items-start gap-3 hover:bg-slate-50/60 transition-colors"
                                    >
                                        <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-xs flex-shrink-0">
                                            {c.name
                                                .split(" ")
                                                .slice(0, 2)
                                                .map((w) => w[0])
                                                .join("")}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="font-medium text-slate-800 text-sm truncate">
                                                    {c.name}
                                                </span>
                                                <span className="text-[11px] text-slate-400 flex-shrink-0">{c.time}</span>
                                            </div>
                                            <p className="text-xs text-slate-500 truncate mt-0.5">{c.lastMessage}</p>
                                        </div>
                                        {c.unread > 0 && (
                                            <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-1.5" />
                                        )}
                                    </button>
                                ))}
                            </div>
                            <div className="px-6 py-3 border-t border-slate-100">
                                <button className="w-full text-center text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center gap-1">
                                    Open all chats <ChevronRight size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Quick summary card */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
                            <h2 className="font-bold text-slate-900 text-[15px]">This Month</h2>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <TrendingUp size={14} /> Revenue
                                </span>
                                <span className="font-semibold text-slate-900">
                                    {currency(stats.revenueThisMonth)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <FileText size={14} /> Quotations sent
                                </span>
                                <span className="font-semibold text-slate-900">27</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <CheckCircle2 size={14} /> Win rate
                                </span>
                                <span className="font-semibold text-slate-900">41%</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                    <Clock size={14} /> Avg. response time
                                </span>
                                <span className="font-semibold text-slate-900">2.4 hrs</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}